import { ValidatorErrorMessages, validateIsTrimmed, validateMaxLength, validateMinLength } from "@civicscience/chops";
import { validateAuth0Policy, validatePasswordMaxLength, validatePasswordMinLength } from "utils/auth";
import { validateIsDateAfter, validateIsDateBefore } from "utils/dates";

import { RegisterOptions } from "react-hook-form";
import { RelativeDateFilterUnit } from "types/dates/date-types";
import { RelativeDateMaxValues } from "consts";
import { SyntheticEvent } from "react";
import { validateMaxDecimalPoints } from "utils/numbers";

const { ErrFieldRequired } = ValidatorErrorMessages;

type BaseValidationProps = {
  required?: boolean;
  validate?: RegisterOptions["validate"];
  onChange?: (e: SyntheticEvent) => void;
};

type NumberProps = BaseValidationProps & {
  min?: number;
  max: number;
  maxDecimalPlaces?: number;
};

type RelativeDateProps = BaseValidationProps & {
  min?: number;
  unit: RelativeDateFilterUnit;
};

type CheckboxProps = BaseValidationProps;

type SelectProps = BaseValidationProps;

type SelectSearchProps = BaseValidationProps;

type CheckButtonGroupProps = BaseValidationProps;

type RadioButtonGroupProps = BaseValidationProps;

type TextProps = BaseValidationProps & {
  minLength?: number;
  maxLength?: number;
  isTrimmed?: boolean;
};

type DateProps = BaseValidationProps & {
  min?: Date;
  max?: Date;
};

type StartDateProps = DateProps & {
  endDate: Date | null | undefined;
};

type EndDateProps = DateProps & {
  startDate: Date | null | undefined;
};

type PasswordProps = BaseValidationProps;

/**
 * Helper to apply our standard "required" text when a control is marked required.
 * The return of this function is designed to be spread into your `RegisterOptions`.
 */
const getRequiredValidation = (isRequired: boolean | null | undefined) =>
  isRequired ? { required: ErrFieldRequired } : {};

/**
 * Helper to build the min validation message for a number based on both `min` and `max` values.
 */
const getMinNumberValidation = (min: number | null | undefined, max: number | null | undefined) =>
  typeof min === "number" && typeof max === "number"
    ? { value: min, message: `Must be a number between ${min} and ${max}` }
    : typeof min === "number"
      ? { value: min, message: `Must be a number greater than ${min - 1}` }
      : undefined;

/**
 * Helper to build the max validation message for a number based on both `min` and `max` values.
 */
const getMaxNumberValidation = (min: number | null | undefined, max: number | null | undefined) =>
  typeof min === "number" && typeof max === "number"
    ? { value: max, message: `Must be a number between ${min} and ${max}` }
    : typeof max === "number"
      ? { value: max, message: `Must be a number less than ${max + 1}` }
      : undefined;

/**
 * Builds `rules` for a number input based on the provided args.
 * The `max` is **required** but `min` is defaulted to 0.
 * We also set `valueAsNumber` to true so that hook form treats this as a number.
 */
export const asNumber = ({
  required = false,
  min = 0,
  max,
  maxDecimalPlaces,
  validate = {},
  ...restProps
}: NumberProps): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  ...restProps,
  min: getMinNumberValidation(min, max),
  max: getMaxNumberValidation(min, max),
  valueAsNumber: true,
  validate: {
    ...validate,
    ...(maxDecimalPlaces ? { validateMaxDecimalPoints: (val) => validateMaxDecimalPoints(val, maxDecimalPlaces) } : {}),
  },
});

/**
 * Builds `rules` for a number input for relative date filters based on the provided args.
 * The `unit` is required to determine the appropriate max value for the input.
 * The `min` is defaulted to 0.
 * We also set `valueAsNumber` to true so that hook form treats this as a number.
 */
export const asRelativeDateNumber = ({
  required = false,
  min = 0,
  unit,
  validate = {},
  ...restProps
}: RelativeDateProps): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  ...restProps,
  min: getMinNumberValidation(min, RelativeDateMaxValues[unit]),
  max: getMaxNumberValidation(min, RelativeDateMaxValues[unit]),
  valueAsNumber: true,
  validate,
});

/**
 * Builds `rules` for a text input based on the provided args.
 * By default we set the following rules (which can be overridden):
 *
 *  - minLength - 0
 *  - maxLength - 250
 *  - isTrimmed - true (validates no leading or trailing whitespace)
 *
 * The `isTrimmed` validation is applied via the custom `validate` record accepted by hook form.
 * Note: You can always spread the result of this function to alter it further as needed.
 */
export const asText = ({
  required = false,
  maxLength = 250,
  minLength = 0,
  isTrimmed = true,
  validate = {},
  ...restProps
}: TextProps = {}): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  minLength: validateMinLength(minLength),
  maxLength: validateMaxLength(maxLength),
  ...restProps,
  validate: {
    ...validate,
    ...(isTrimmed ? { validateIsTrimmed } : {}),
  },
});

/**
 * Builds `rules` for a checkbox input.
 */
export const asCheckbox = ({
  required = false,
  validate,
  ...restProps
}: CheckboxProps = {}): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  ...restProps,
  validate,
});

/**
 * Builds `rules` for a select input.
 * Defaults `required` to false.
 */
export const asSelect = ({ required = false, validate, ...restProps }: SelectProps = {}): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  ...restProps,
  validate,
});

/**
 * Builds `rules` for a select search control.
 * Defaults `required` to false.
 */
export const asSelectSearch = ({ required = false, validate }: SelectSearchProps = {}): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  validate,
});

/**
 * Builds `rules` for a check button group control.
 * Defaults `required` to false.
 */
export const asCheckButtonGroup = ({
  required = false,
  validate,
  ...restProps
}: CheckButtonGroupProps = {}): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  ...restProps,
  validate,
});

/**
 * Builds `rules` for a radio button group control.
 * Defaults `required` to false.
 */
export const asRadioButtonGroup = ({
  required = false,
  validate,
}: RadioButtonGroupProps = {}): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  validate,
});

/**
 * Builds `rules` for a Datepicker input.
 * Defaults `required` to false.
 * We also set `valueAsDate` to true so that hook form treats this as a Date.
 */
export const asDate = ({ required = false, min, max, validate }: DateProps = {}): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  valueAsDate: true,
  validate: {
    ...validate,
    ...(min ? { dateIsAfter: (val) => validateIsDateAfter(val, min) } : {}),
    ...(max ? { dateIsBefore: (val) => validateIsDateBefore(val, max) } : {}),
  },
});

/**
 * Wrapper over `asDate` which applies special validation for a start date.
 * Note: You can always spread the result of this function to alter it further as needed.
 */
export const asStartDate = ({ endDate, validate, ...rest }: StartDateProps): Partial<RegisterOptions> =>
  asDate({
    ...rest,
    validate: {
      ...validate,
      dateIsBeforeEnd: (val: Date) => (endDate && val ? validateIsDateBefore(val, endDate) : true),
    },
  });

/**
 * Wrapper over `asDate` which applies special validation for an end date.
 * Note: You can always spread the result of this function to alter it further as needed.
 */
export const asEndDate = ({ startDate, validate, ...rest }: EndDateProps): Partial<RegisterOptions> =>
  asDate({
    ...rest,
    validate: {
      ...validate,
      dateIsAfterStart: (val: Date) => (startDate && val ? validateIsDateAfter(val, startDate) : true),
    },
  });

export const asAuth0EligiblePassword = ({
  required = true,
  validate,
  ...restProps
}: PasswordProps): Partial<RegisterOptions> => ({
  ...getRequiredValidation(required),
  minLength: validatePasswordMinLength(8),
  maxLength: validatePasswordMaxLength(50),
  ...restProps,
  validate: {
    ...{ validateIsTrimmed },
    validateAuth0Policy: (val: string) => validateAuth0Policy(val),
    ...validate,
  },
});
